import axios, { AxiosError } from 'axios';
import { BASE_URL, SEASON_API_PATH } from 'constants/api';
import { ApiService } from 'service/api/apiService';
import { seasonApiService } from 'service/api/seasonApiService';
import { FetchResult } from 'type/apiType';
import {
  ElevenDbSeasonInterface,
  PosterInterface,
  SeasonApiRequest,
  SeasonData,
  SeasonInterface,
  SeasonListItem,
} from 'type/seasonType';
import { I18nApiErrorHandler } from '../../../shared/services/i18n/i18nApiErrorHandler';

class SeasonService {
  constructor(private apiService: ApiService<SeasonInterface>) {}

  public async fetch(query?: URLSearchParams): Promise<FetchResult<SeasonInterface>> {
    return this.apiService.fetch(query);
  }

  public async fetchById(
    seasonId: number,
    query?: Record<string, string>
  ): Promise<SeasonInterface> {
    if (!seasonId) {
      return {} as SeasonInterface;
    }
    const response = await this.apiService.fetchById(seasonId, query);
    return response;
  }

  public async fetchByIds(
    seasonIds: number[],
    query?: Record<string, string>
  ): Promise<FetchResult<SeasonInterface>> {
    if (seasonIds.length === 0) {
      return {
        results: [],
        count: 0,
        next: null,
        previous: null,
        page_next: null,
        page_previous: null,
        page_size: 0,
      };
    }
    return await this.apiService.fetchByIds(seasonIds, query);
  }

  public async create(season: SeasonInterface): Promise<SeasonInterface> {
    return await this.apiService.create(season);
  }

  public async update(seasonId: number, season: Partial<SeasonInterface>): Promise<void> {
    await this.apiService.update(seasonId, season);
  }

  public async delete(seasonId: number): Promise<void> {
    await this.apiService.delete(seasonId);
  }

  /**
   * i18n 필드를 포함한 Season 목록 조회
   */
  public async fetchSeasonsWithI18n(
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeasonData>> {
    return seasonApiService.fetchSeasonListWithI18n(queryParams);
  }

  /**
   * i18n 필드를 포함한 단일 Season 조회
   */
  public async fetchSeasonWithI18n(id: number): Promise<SeasonData> {
    return seasonApiService.fetchSeasonWithI18n(id);
  }

  /**
   * 레거시 필드와 i18n 필드를 모두 포함한 Season 생성
   */
  public async createSeasonWithI18n(seasonData: SeasonApiRequest): Promise<SeasonData> {
    return seasonApiService.createSeasonWithI18n(seasonData);
  }

  /**
   * 레거시 필드와 i18n 필드를 모두 포함한 Season 업데이트
   */
  public async updateSeasonWithI18n(
    id: number,
    seasonData: Partial<SeasonApiRequest>
  ): Promise<SeasonData> {
    return seasonApiService.updateSeasonWithI18n(id, seasonData);
  }

  /**
   * 특정 언어의 Season 목록 조회
   */
  public async fetchSeasonsByLanguage(
    language: string,
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeasonData>> {
    return seasonApiService.fetchSeasonsByLanguage(language, queryParams);
  }

  /**
   * 번역 완성도별 Season 조회
   */
  public async fetchSeasonsByTranslationStatus(
    requiredLanguages: string[] = ['ko'],
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeasonData>> {
    return seasonApiService.fetchSeasonsByTranslationStatus(requiredLanguages, queryParams);
  }

  /**
   * 특정 Series에 속한 Season 목록 조회 (i18n 지원)
   */
  public async fetchSeasonsBySeriesWithI18n(
    seriesId: number,
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeasonData>> {
    return seasonApiService.fetchSeasonsBySeriesWithI18n(seriesId, queryParams);
  }

  /**
   * 리스트 페이지용 최소 필드만 포함하는 Season 목록 조회
   * SeasonListSerializer 사용
   */
  public async fetchSeasonList(
    queryParams?: URLSearchParams
  ): Promise<FetchResult<SeasonListItem>> {
    try {
      const params = new URLSearchParams(queryParams);

      // SeasonListSerializer 사용 (백엔드에서 자동으로 적용)
      params.set('serializer', 'list');

      const response = await axios.get(`${BASE_URL}${SEASON_API_PATH}`, { params });
      return response.data;
    } catch (error) {
      const i18nErrors = I18nApiErrorHandler.handleAxiosError(error as AxiosError);
      throw new Error(I18nApiErrorHandler.formatErrorsForUser(i18nErrors));
    }
  }

  public async bulkDistribution(ids: number[], contentConsumerName: string): Promise<void> {
    await axios.post(`${BASE_URL}${SEASON_API_PATH}bulk-distribution/`, {
      ids,
      content_consumer_name: contentConsumerName,
    });
  }

  public async fetchPoster(seasonId: number): Promise<PosterInterface[]> {
    const response = await axios.get(`${BASE_URL}${SEASON_API_PATH}${seasonId}/poster/`);
    return response.data;
  }

  public async uploadPoster(seasonId: number, posters: PosterInterface[]): Promise<void> {
    await Promise.all(
      posters.map(async (poster: PosterInterface) => {
        const formData = new FormData();
        formData.append('file', poster.originFileObj!);
        formData.append('is_representative', poster.is_representative.toString());
        if (poster.language) {
          formData.append('language', poster.language);
        }
        if (poster.orientation) {
          formData.append('orientation', poster.orientation);
        }
        const response = await axios.post(
          `${BASE_URL}${SEASON_API_PATH}${seasonId}/poster/`,
          formData
        );
        return response.data;
      })
    );
  }

  public async removePoster(seasonId: number, posters: PosterInterface[]): Promise<void> {
    await Promise.all(
      posters.map(async (poster: PosterInterface) => {
        await axios.delete(`${BASE_URL}${SEASON_API_PATH}${seasonId}/poster/${poster.id}/`);
      })
    );
  }

  public async updatePoster(seasonId: number, poster: PosterInterface): Promise<void> {
    await axios.patch(`${BASE_URL}${SEASON_API_PATH}${seasonId}/poster/${poster.id}/`, poster);
  }

  public async fetchElevenDbSeason(seasonId: number): Promise<ElevenDbSeasonInterface> {
    return (await this.apiService.fetchChildItemsByParentId<ElevenDbSeasonInterface>(
      seasonId,
      'eleven-db/'
    )) as ElevenDbSeasonInterface;
  }

  public async updateElevenDbSeason(
    seasonId: number,
    elevenDbSeason: Partial<ElevenDbSeasonInterface>
  ): Promise<void> {
    await this.apiService.updateItemsForParent(seasonId, 'eleven-db/', elevenDbSeason);
  }

  public async createElevenDbSeason(
    seasonId: number,
    elevenDbSeason: ElevenDbSeasonInterface
  ): Promise<void> {
    await this.apiService.createItemsForParent(seasonId, 'eleven-db/', elevenDbSeason);
  }
}

export const seasonService = new SeasonService(new ApiService<SeasonInterface>(SEASON_API_PATH));
